import { useState, useCallback, useRef, useEffect } from 'react';
import { searchForDuplicates, type DuplicateDetectionResult } from '../services/duplicateDetectionService';
import { normFiscal, phoneVariants, searchableEmail, type DuplicateCriteria } from '../services/duplicateMatching';

const DEBOUNCE_MS = 600;

/**
 * Controllo doppioni mentre si compila un lead/cliente (creazione e modifica).
 * Avviso, non blocco: il chiamante mostra `duplicateResult`. Ricerca dopo una
 * pausa di digitazione, vale sempre l'ultima risposta; svuotati i campi
 * l'avviso sparisce.
 */
export function useDuplicateDetection() {
  const [isChecking, setIsChecking] = useState(false);
  const [duplicateResult, setDuplicateResult] = useState<DuplicateDetectionResult | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastKeyRef = useRef('');
  const requestRef = useRef(0);

  const cancelTimer = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  };
  useEffect(() => cancelTimer, []);

  /** `criteria`: i campi da controllare (in modifica solo quelli cambiati). */
  const triggerDuplicateCheck = useCallback((criteria: DuplicateCriteria, options: { excludeClientId?: string | null } = {}) => {
    const c: DuplicateCriteria = {
      email: searchableEmail(criteria.email),
      phone: phoneVariants(criteria.phone).length ? criteria.phone : null,
      vat_number: normFiscal(criteria.vat_number) || null,
      tax_code: normFiscal(criteria.tax_code) || null,
    };
    const key = JSON.stringify([c.email, c.phone, c.vat_number, c.tax_code, options.excludeClientId || null]);
    if (key === lastKeyRef.current) return;
    lastKeyRef.current = key;
    cancelTimer();
    const request = ++requestRef.current;
    if (!c.email && !c.phone && !c.vat_number && !c.tax_code) {
      setDuplicateResult(null);
      setIsChecking(false);
      return;
    }
    timerRef.current = setTimeout(async () => {
      setIsChecking(true);
      try {
        const result = await searchForDuplicates(c, options);
        if (request === requestRef.current) setDuplicateResult(result);
      } catch (error) {
        // Il controllo e' solo un aiuto: in caso di errore si prosegue senza avviso
        console.error('Error checking for duplicates:', error);
      } finally {
        if (request === requestRef.current) setIsChecking(false);
      }
    }, DEBOUNCE_MS);
  }, []);

  const resetDuplicateCheck = useCallback(() => {
    cancelTimer();
    requestRef.current++;
    lastKeyRef.current = '';
    setDuplicateResult(null);
    setIsChecking(false);
  }, []);

  return { isChecking, duplicateResult, triggerDuplicateCheck, resetDuplicateCheck };
}
